import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

// styles and images
import "../../project/Project.css";
import Close from "../../../assets/close.svg";

// data
import { monthOptions } from "../../../data/collections-data";

// components and global
import {
  accountDataStore,
  companyStore,
  dbDataStore,
  messageAndErrorStore,
  userStore,
} from "../../../store/Store";
import FormButton from "../../../formComponents/FormButton";
import DropDown from "../../../formComponents/DropDown";
import InlineInput from "../../../formComponents/InlineInput";

// hooks
import { useVerifyUnique } from "../../../hooks/useVerifyUnique";
import { useEscape } from "../../../hooks/useEscape";
import { useGetOptions } from "../../../hooks/useGetOptions";

// funcs
import {
  getFormattedDate,
  monthNumberOfDays,
  parseDateInCentral,
} from "../../../funcs/helperFuncs";

export default function AddAccountCustomerTieredInvoice({ setTriggerInvoice }) {
  // global
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const currentCompany = userStore((data) => data.currentCompany);
  const companyData = dbDataStore((data) => data.companyData);

  const accountCustomerInvoices = accountDataStore(
    (data) => data.accountCustomerInvoices,
  );
  const setAccountCustomerInvoices = accountDataStore(
    (data) => data.setAccountCustomerInvoices,
  );
  const setAccountCustomerInvoiceTotals = accountDataStore(
    (data) => data.setAccountCustomerInvoiceTotals,
  );
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const setInvoiceCounts = companyStore((data) => data.setInvoiceCounts);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // local
  const [pending, setPending] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);
  const [fIdx, setFIdx] = useState(null);
  const [projectData, setProjectData] = useState({
    projects: null,
    tiers: null,
    weight: null,
  });
  const [projectTiers, setProjectTiers] = useState();
  const [customerInvoice, setCustomerInvoice] = useState({
    id: 0,
    customer_id: 0,
    invoice_name: "",
    company_id: currentCompany.id,
    is_asset: false,
    start_date: null,
    end_date: null,
    loads_start: null,
    loads_end: null,
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
    is_tier: true,
    tier_month: 0,
    tier_year: 0,
  });

  useGetOptions(setCustomerOptions, companyData, ...Array(2), "company_name");
  
  const navigate = useNavigate();
  const { exists } = useVerifyUnique(
    null,
    "invoice_name",
    customerInvoice.invoice_name,
    "",
    accountCustomerInvoices,
    "customer_invoices"
  );
  const handleClose = () => {
    setTriggerInvoice(false);
  };

  useEscape(setTriggerInvoice);

  // get projects, tiers and weights
  useEffect(() => {
    if (
      customerInvoice.customer_id > 0 &&
      customerInvoice.tier_month > 0 &&
      customerInvoice.tier_year.length === 4
    ) {
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);

      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };
      let day = monthNumberOfDays(
        customerInvoice.tier_month,
        parseInt(customerInvoice.tier_year),
      );
      let startStr = `${customerInvoice.tier_year}-${customerInvoice.tier_month}-01`;
      let endStr = `${customerInvoice.tier_year}-${customerInvoice.tier_month}-${day}`;
      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/customer-project-tiers/${customerInvoice.customer_id}?loads_start=${startStr}&loads_end=${endStr}`,
        requestOptions,
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 403) {
              setAuthStatus(false);
              navigate("/login");
            } else {
              toggleNewMessage("Could not get projects");
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            return;
          } else {
            setProjectData(data);
          }
        })
        .catch((err) => {
          console.log(err);
          toggleNewMessage("Something went wrong");
          return;
        });
      return () => controller.abort();
    }
  }, [
    customerInvoice.customer_id,
    customerInvoice.tier_month,
    customerInvoice.tier_year,
    JwtToken,
    navigate,
    setAuthStatus,
    toggleNewMessage,
  ]);
  // get tier options
  useEffect(() => {
    if (projectData.tiers && projectData.projects) {
      var arr = [];
      var projArr = [];
      projectData.projects.forEach((p, idx) => {
        arr.push([]);
        projArr.push({ project_id: p.id, tier_id: 0, weight: 0, tier_number: 0 });
        projectData.tiers[idx].forEach((t) => {
          arr[idx].push({ value: t.id, label: t.tier_number });
        });
      });
    }
    setTierOptions(arr);
    setProjectTiers(projArr);
  }, [projectData.tiers, projectData.projects]);

  const handleChange = (e) => {
    setCustomerInvoice({
      ...customerInvoice,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelect = (option, e) => {
    setCustomerInvoice({
      ...customerInvoice,
      [e.name]: option.value,
    });
  };

  const handleTiers = (option, idx, isWeight) => {
    if (!isWeight) {
      setProjectTiers((currentData) => {
        const newData = currentData;
        newData[idx].tier_id = option.value;
        newData[idx].tier_number = option.label;
        return newData;
      });
    } else {
      setProjectTiers((currentData) => {
        const newData = currentData;
        newData[idx].weight = parseFloat(option.target.value);
        return newData;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);
    if (exists) {
      toggleNewMessage(
        "This invoice already exists, please update the name/version",
      );
      setPending(false);
      return;
    }
    let yrLen = customerInvoice.tier_year.length;
    if (yrLen !== 4) {
      toggleNewMessage("The year must be 4 digits");
      setPending(false);
      return;
    }
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const reqBody = {
      invoice: customerInvoice,
      project_tiers: projectTiers,
    };

    reqBody.invoice.tier_year = parseInt(customerInvoice.tier_year);
    let day = monthNumberOfDays(
      customerInvoice.tier_month,
      parseInt(customerInvoice.tier_year),
    );
    let startStr = `${customerInvoice.tier_year}-${customerInvoice.tier_month}-01`;
    let endStr = `${customerInvoice.tier_year}-${customerInvoice.tier_month}-${day}`;

    reqBody.invoice.start_date = getFormattedDate(startStr);
    reqBody.invoice.end_date = getFormattedDate(endStr);
    reqBody.invoice.loads_start = parseDateInCentral(startStr, false);
    reqBody.invoice.loads_end = parseDateInCentral(endStr, true);
    console.log(reqBody);
    const requestOptions = {
      method: "POST",
      headers,
      body: JSON.stringify(reqBody),
      signal: controller.signal,
    };
    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/account-customer-tiered-invoice/${customerInvoice.id}`,
      requestOptions,
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setPending(false);
            navigate("/login");
            setAuthStatus(false);
            return Promise.resolve();
          } else {
            setPending(false);
            return Promise.resolve();
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data) {
          setAccountCustomerInvoices(data.customer_invoices);
          setAccountCustomerInvoiceTotals(data.customer_invoice_totals);
          setInvoiceCounts(data.invoice_counts);
          toggleNewMessage(null, "Invoice Added");
          setPending(false);
          setTriggerInvoice(false);
        } else {
          toggleNewMessage("No associated loads with date range");
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setPending(false));
    return () => controller.abort();
  };

  return (
    <div className="popup">
      <div className="tier-popup-inner">
        {!pending && (
          <div className="close">
            <img onClick={handleClose} src={Close} alt="close" />
          </div>
        )}
        <h3>Generate Customer Monthly Tier Invoice</h3>
        <form onSubmit={handleSubmit}>
          <DropDown
            name={"customer_id"}
            title={"Customer"}
            options={customerOptions}
            onChange={handleSelect}
            classFour="dropdown-wide"
            maxMenuHeight={200}
            required={true}
            value={customerOptions.filter(
              (x) => x.value === customerInvoice.customer_id,
            )}
            classOne="mb-3 row field"
            classTwo="col-form-label bold-label col-sm-4"
          />
          <DropDown
            name={"tier_month"}
            title={"Month"}
            classFour="dropdown-wide"
            options={monthOptions}
            onChange={handleSelect}
            maxMenuHeight={200}
            required={true}
            value={monthOptions.filter(
              (x) => x.value === customerInvoice.tier_month,
            )}
            classOne="mb-3 row field"
            classTwo="col-form-label bold-label col-sm-4"
          />
          <div className="row g-1">
            <InlineInput
              type={"number"}
              name={"tier_year"}
              title={"Year"}
              onChange={handleChange}
              required={true}
              classname="col-form-label bold-label col-sm-4"
              inputClass="col-3"
              placeholder="YYYY"
            />
            {projectData.projects && tierOptions && (
              <>
                <InlineInput
                  type={"text"}
                  name={"invoice_name"}
                  title={"Invoice Name"}
                  placeholder={"Name of Invoice"}
                  onChange={handleChange}
                  value={customerInvoice.invoice_name}
                  divClass="row mb-3"
                  classname="col-form-label bold-label col-sm-4"
                  required={true}
                />
                <div className="row mb-3">
                  <h4>Job Details</h4>
                </div>
                <div className="row ">
                  <p className="bold-label col-sm-4">Job</p>
                  <p className="bold-label col-sm-3">Available Weight</p>
                  <p className="bold-label col-sm-2">Tier</p>
                  <p className="bold-label col-sm-2">Weight</p>
                </div>
                {projectData.projects.map((p, idx) => (
                  <div className="mb-1 row fild" key={idx}>
                    <p className="col-sm-4 col-form-label bold-label">
                      {p.job_name}
                    </p>
                    <p className="col-sm-3 col-form-label bold-label">
                      {parseFloat(projectData.weight[idx]).toFixed(2)}
                    </p>
                    <Select
                      name={"tier_id"}
                      options={tierOptions[idx]}
                      onChange={(o) => handleTiers(o, idx)}
                      className={fIdx === idx ? "bottom-dd" : "dropdown"}
                      maxMenuHeight={200}
                      onFocus={() => setFIdx(idx)}
                      onBlur={() => setFIdx(null)}
                      required={true}
                      placeholder="Select Tier"
                    />
                    <input
                      type={"number"}
                      step={"0.01"}
                      name={"tier_weight_tons"}
                      onChange={(e) => handleTiers(e, idx, true)}
                      required={true}
                      className="col-sm-2"
                      placeholder="Total Weight"
                    />
                  </div>
                ))}
              </>
            )}
          </div>
          <FormButton name={"Submit"} pending={pending} />
        </form>
      </div>
    </div>
  );
}
